/**
 * ShapeShift Tasks — intent classifier + name reader for the adaptive task input. Due dates are NOT parsed
 * here: the component asks the shared parser (src/utils/chrono-simple.js, the one TaskComposer uses) and
 * hands the matched phrase in as `dateText`.
 *
 * KEEP THIS FILE IMPORT-FREE AND ESM-ONLY (`export function`, never `module.exports`):
 *   - import-free  -> cypress/node-tests/shapeshift-classifier.test.js executes this exact file
 *     (it strips the `export` keywords, the same way phi-mask-core.test.js loads phiMask.js);
 *   - ESM-only     -> a CommonJS file under src/ compiles but throws at runtime in the webpack bundle
 *     ("Cannot assign to read only property 'exports'") and `yarn build` does NOT catch it.
 */

const TIME_PATTERN = /\b(today|tomorrow|tonight|monday|tuesday|wednesday|thursday|friday|saturday|sunday|next week|this week|next month|morning|afternoon|evening|eod|by \d|due \d|\d+(am|pm)|in (\d+|an?|one|two|three|four|five|six|seven|eight|nine|ten) (day|week|month)s?|(\d+|an?|one|two|three|four|five|six|seven|eight|nine|ten|couple of|few) (day|week|month)s? (later|out|from now|from today|hence)|(early|mid|late|end of)[ -](next|this) week)/i;
const PATIENT_PATTERN = /#|\bpatient\b|\bpt\b|\bpatient'?s\b/i;
const TEAM_PATTERN = /@|\bassign\b|\btell\b|\bask\b|\bremind\b|\bhave\b.*\bdo\b|\bget\b.*\bto\b/i;

// Words that are never treated as a person's name (task verbs, glue words, time words).
const STOP_WORDS = new Set([
  'call', 'calls', 'ask', 'tell', 'remind', 'follow', 'followup', 'order', 'update', 'send', 'schedule',
  'email', 'text', 'contact', 'review', 'assign', 'check', 'get', 'have', 'make', 'notify', 'add', 'set',
  'fix', 'finish', 'see', 'let', 'put', 'say', 'need', 'needs', 'please', 'also', 'then', 'again',
  'the', 'this', 'that', 'and', 'with', 'for', 'about', 'from', 'into', 'onto', 'will', 'would', 'can',
  'could', 'should', 'not', 'but', 'you', 'your', 'our', 'all', 'any', 'new', 'due', 'back', 'out', 'off',
  'over', 'when', 'what', 'who', 'how', 'why', 'before', 'after', 'by',
  'patient', 'patients', 'pt', 'team', 'task', 'appointment', 'appt', 'insurance', 'lab', 'form', 'forms',
  'chart', 'claim',
  'today', 'tomorrow', 'tonight', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
  'sunday', 'next', 'week', 'month', 'morning', 'afternoon', 'evening', 'eod',
]);

/**
 * Returns one of: 'REMINDER' | 'TEAM' | 'PATIENT' | 'GENERIC'
 * Priority: PATIENT > TEAM > REMINDER > GENERIC
 */
export function classifyTaskIntent(text) {
  if (!text || !text.trim()) return 'GENERIC';
  if (PATIENT_PATTERN.test(text)) return 'PATIENT';
  if (TEAM_PATTERN.test(text)) return 'TEAM';
  if (TIME_PATTERN.test(text)) return 'REMINDER';
  return 'GENERIC';
}

// Where the date phrase sits in the text (case-insensitive), or null.
function phraseRange(str, phrase) {
  const p = String(phrase == null ? '' : phrase).trim();
  if (!p) return null;
  const start = str.toLowerCase().indexOf(p.toLowerCase());
  return start === -1 ? null : { start, end: start + p.length };
}

const WORD_RE = /([@#]?)([A-Za-z0-9À-ɏ][A-Za-z0-9À-ɏ'’]*)/g;
const NAME_SPLIT_RE = /[^a-z0-9À-ɏ']+/;
const POSSESSIVE_RE = /['’]s$/i;

// Words inside dateRange belong to the due date ("day" in "day after tomorrow"), never to a name.
function tokenizeWords(str, dateRange) {
  const out = [];
  const re = new RegExp(WORD_RE.source, 'g');
  let m;
  while ((m = re.exec(str)) !== null) {
    const raw = m[2].replace(POSSESSIVE_RE, '').replace(/['’]+$/, '');
    const word = raw.toLowerCase();
    const sigil = m[1];
    const end = m.index + m[0].length;
    out.push({
      raw,
      word,
      sigil,
      // The last word is still being typed when nothing (space, punctuation) follows it.
      typing: end === str.length,
      skip: !sigil && (word.length < 3 || /^\d+$/.test(word) || STOP_WORDS.has(word)
        || (!!dateRange && m.index >= dateRange.start && end <= dateRange.end)),
    });
  }
  return out;
}

function nameTokens(value) {
  return String(value == null ? '' : value)
    .toLowerCase()
    .split(NAME_SPLIT_RE)
    .map(t => t.replace(POSSESSIVE_RE, '').replace(/^'+|'+$/g, ''))
    .filter(Boolean);
}

function employeeEntry(emp) {
  if (!emp || typeof emp !== 'object') return null;
  const base = emp.Name || String(emp.Username || '').split('@')[0];
  const tokens = nameTokens(base);
  return tokens.length ? { ref: emp, tokens } : null;
}

function patientEntry(pat) {
  if (!pat || typeof pat !== 'object') return null;
  const tokens = nameTokens(`${pat.FName || ''} ${pat.LName || ''}`);
  return tokens.length ? { ref: pat, tokens } : null;
}

// A finished word must equal a name token; the word still being typed may also be a prefix (3+ letters,
// or 1+ after an @).
function employeeTokenMatches(entry, tok) {
  const minPrefix = tok.sigil ? 1 : 3;
  return entry.tokens.some(t => t === tok.word || (tok.typing && tok.word.length >= minPrefix && t.startsWith(tok.word)));
}

function patientTokenMatches(entry, tok) {
  return entry.tokens.includes(tok.word);
}

// Longest run of ADJACENT words starting at each eligible word that all belong to one entry's name.
function findRuns(tokens, entries, kind, canStart, matches) {
  const runs = [];
  if (!entries.length) return runs;
  for (let i = 0; i < tokens.length; i++) {
    const first = tokens[i];
    if (!canStart(first)) continue;
    let cand = entries.filter(e => matches(e, first));
    if (!cand.length) continue;
    let j = i;
    while (j + 1 < tokens.length) {
      const next = tokens[j + 1];
      if (next.skip || next.sigil) break;
      const narrowed = cand.filter(e => matches(e, next));
      if (!narrowed.length) break;
      cand = narrowed;
      j++;
    }
    runs.push({ kind, i, j, len: j - i + 1, explicit: !!first.sigil, cand: cand.map(e => e.ref) });
  }
  return runs;
}

// First Title-Case name (not the sentence's opening word, not one still being typed) — the "who do you mean?" trigger.
function findUnmatchedName(tokens) {
  const isName = (t, idx) => !t.skip && !t.sigil && /^[A-Z]/.test(t.raw) && !(t.typing && idx === tokens.length - 1);
  for (let i = 0; i < tokens.length; i++) {
    if (!isName(tokens[i], i)) continue;
    let j = i;
    while (j + 1 < tokens.length && isName(tokens[j + 1], j + 1)) j++;
    if (i === 0 && j === 0) continue;
    return tokens.slice(i, j + 1).map(t => t.raw).join(' ');
  }
  return null;
}

/**
 * Finds the people mentioned in free text by matching it against the loaded lists — case-insensitive,
 * so "call saumil to follow up with mary" works exactly like the capitalized version.
 *
 * Employees are checked first (smaller list). `@word` forces an employee, `#word` forces a patient.
 * Returns { employee, employeeCandidates, employeeWords, patientHint, unmatchedName }:
 *   employee            the one matching employee, or null
 *   employeeCandidates  2+ employees when the text fits several (never silently picked), else []
 *   patientHint         text to pre-fill the patient search with, or null
 *   unmatchedName       a capitalized name that matches nobody (the "teammate or patient?" question), or null
 * opts.preferPatient: a name that is both an employee and a patient goes to the patient (text said "patient"/"pt").
 * opts.dateText: the due-date phrase found in the text (from chrono-simple); its words are never read as names.
 */
export function resolveNames(text, employees, patients, opts) {
  const result = { employee: null, employeeCandidates: [], employeeWords: '', patientHint: null, unmatchedName: null };
  const str = text == null ? '' : String(text);
  if (!str.trim()) return result;

  const tokens = tokenizeWords(str, phraseRange(str, opts && opts.dateText));
  const hasAt = tokens.some(t => t.sigil === '@');
  const hasHash = tokens.some(t => t.sigil === '#');
  const preferPatient = !!(opts && opts.preferPatient);

  const empEntries = (Array.isArray(employees) ? employees : []).map(employeeEntry).filter(Boolean);
  const patEntries = tokens.some(t => !t.skip && t.sigil !== '@') && Array.isArray(patients)
    ? patients.map(patientEntry).filter(Boolean)
    : [];

  const runs = findRuns(tokens, empEntries, 'E', t => !t.skip && t.sigil !== '#' && (!hasAt || t.sigil === '@'), employeeTokenMatches)
    .concat(findRuns(tokens, patEntries, 'P', t => !t.skip && t.sigil !== '@' && (!hasHash || t.sigil === '#'), patientTokenMatches));

  const rank = r => (r.kind === 'P') === preferPatient ? 0 : 1;
  runs.sort((a, b) => (b.explicit - a.explicit) || (b.len - a.len) || (rank(a) - rank(b)) || (a.i - b.i));

  const overlaps = (a, b) => a.i <= b.j && b.i <= a.j;
  let bestE = null;
  let bestP = null;
  for (const run of runs) {
    if (run.kind === 'E' && !bestE && !(bestP && overlaps(run, bestP))) bestE = run;
    else if (run.kind === 'P' && !bestP && !(bestE && overlaps(run, bestE))) bestP = run;
  }

  const words = run => tokens.slice(run.i, run.j + 1).map(t => t.raw).join(' ');

  if (bestE) {
    result.employeeWords = words(bestE);
    if (bestE.cand.length === 1) result.employee = bestE.cand[0];
    else result.employeeCandidates = bestE.cand;
  }
  if (bestP) {
    result.patientHint = words(bestP);
  } else if (hasHash) {
    result.patientHint = tokens.find(t => t.sigil === '#').raw;
  }
  if (!bestE && !bestP && !hasAt && !hasHash) result.unmatchedName = findUnmatchedName(tokens);
  return result;
}

function nameSpan(text, words, type) {
  const parts = String(words || '').split(/\s+/).filter(Boolean)
    .map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (!parts.length) return null;
  const re = new RegExp('(?:^|[^A-Za-z0-9@#])([@#]?' + parts.join('[^A-Za-z0-9]+') + ')(?![A-Za-z0-9])', 'i');
  const m = re.exec(text);
  if (!m) return null;
  const start = m.index + m[0].indexOf(m[1]);
  return { type, start, end: start + m[1].length };
}

/**
 * Splits the typed text into [{ type: 'text'|'emp'|'pat'|'date', text }] so the card can show which words
 * were read as a teammate, a patient or a due date. `resolved` is the resolveNames() result;
 * `dateText` is the phrase the shared date parser (chrono-simple) read the due date from.
 */
export function findDetectedSpans(text, resolved, dateText) {
  const str = text == null ? '' : String(text);
  const spans = [];
  const res = resolved || {};
  const emp = nameSpan(str, res.employeeWords, 'emp');
  const pat = nameSpan(str, res.patientHint, 'pat');
  if (emp) spans.push(emp);
  if (pat) spans.push(pat);
  const range = phraseRange(str, dateText);
  if (range) spans.push({ type: 'date', start: range.start, end: range.end });
  spans.sort((a, b) => a.start - b.start);
  const out = [];
  let pos = 0;
  for (const s of spans) {
    if (s.start < pos) continue;
    if (s.start > pos) out.push({ type: 'text', text: str.slice(pos, s.start) });
    out.push({ type: s.type, text: str.slice(s.start, s.end) });
    pos = s.end;
  }
  if (pos < str.length) out.push({ type: 'text', text: str.slice(pos) });
  return out;
}
